import React, { useEffect, useRef, useState } from "react";
import "./SyncDestinationPicker.css";
import {
  createDestination,
  DestinationItem,
  listDestinations,
  SyncAdapter,
} from "../utils/api";

interface SyncDestinationPickerProps {
  linkId: string;
  activeAdapter: SyncAdapter;
  presetPageId?: string | null;
  presetPageTitle?: string;
  onSelect: (pageId?: string, adapter?: SyncAdapter) => void;
  onClose: () => void;
}

const SyncDestinationPicker: React.FC<SyncDestinationPickerProps> = ({
  linkId,
  activeAdapter,
  presetPageId,
  presetPageTitle,
  onSelect,
  onClose,
}) => {
  const [adapter, setAdapter] = useState<SyncAdapter>(activeAdapter);
  const [destinations, setDestinations] = useState<DestinationItem[]>([]);
  const [selected, setSelected] = useState<string>(presetPageId ?? "");
  const [query, setQuery] = useState("");
  const [newDestinationTitle, setNewDestinationTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    setSelected(presetPageId ?? "");
  }, [presetPageId]);

  useEffect(() => {
    setAdapter(activeAdapter);
  }, [activeAdapter]);

  useEffect(() => {
    setQuery("");
    void fetchDestinations(adapter, "");
  }, [adapter]);

  useEffect(() => {
    if (loading) {
      return;
    }

    const timer = window.setTimeout(() => {
      void fetchDestinations(adapter, query.trim(), true);
    }, 300);

    return () => window.clearTimeout(timer);
  }, [query, loading, adapter]);

  const fetchDestinations = async (
    selectedAdapter: SyncAdapter,
    searchQuery = "",
    background = false,
  ) => {
    const requestId = ++requestIdRef.current;
    setError(null);

    if (background) {
      setIsSearching(true);
    } else {
      setLoading(true);
    }

    try {
      const loadedDestinations = await listDestinations(selectedAdapter, searchQuery);

      if (requestId !== requestIdRef.current) {
        return;
      }

      setDestinations(loadedDestinations);
    } catch (err) {
      if (requestId !== requestIdRef.current) {
        return;
      }
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setIsSearching(false);
      }
    }
  };

  const handleConfirm = () => {
    if (selected) {
      onSelect(selected, adapter);
      return;
    }
    onSelect(undefined, adapter);
  };

  const handleCreateDestination = async () => {
    if (!newDestinationTitle.trim()) {
      return;
    }
    setIsCreating(true);
    setError(null);

    try {
      const created = await createDestination(adapter, newDestinationTitle.trim());
      setDestinations((prev) => [created, ...prev.filter((item) => item.id !== created.id)]);
      setSelected(created.id);
      setNewDestinationTitle("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create destination");
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="sync-modal-overlay" onClick={onClose}>
      <div className="sync-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Choose sync destination</h3>
        {loading && <div className="sync-loading-state">Loading destinations...</div>}
        {!loading && presetPageId && (
          <p className="sync-current-target">
            Current destination:{' '}
            <strong>{presetPageTitle || 'a page whose name could not be loaded'}</strong>
          </p>
        )}
        {error && (
          <div className="sync-error">
            <p>{error}</p>
            <p>You can still keep or clear the current destination.</p>
          </div>
        )}

        {!loading && (
          <>
            <p className="sync-current-target">
              Active adapter: <strong>{adapter}</strong>
            </p>
            <label className="sync-label">Choose destination</label>
            <input
              className="sync-search"
              placeholder="Search destinations..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <div className="sync-search-status" aria-live="polite">
              <span className={`sync-search-indicator ${isSearching ? "is-active" : ""}`} />
              {isSearching ? "Updating results..." : "Results are up to date"}
            </div>
            <div
              className={`sync-page-list ${isSearching ? "is-refreshing" : ""}`}
              role="listbox"
              aria-label="Sync destinations"
            >
              <button
                type="button"
                className={`sync-page-item ${selected === "" ? "is-selected" : ""}`}
                onClick={() => setSelected("")}
              >
                  <div className="sync-page-title">Skip / Use default routing</div>
                </button>
              {destinations.map((destination) => (
                <button
                  type="button"
                  key={`${destination.adapter}:${destination.id}`}
                  className={`sync-page-item ${selected === destination.id ? "is-selected" : ""}`}
                  onClick={() => setSelected(destination.id)}
                >
                  <div className="sync-page-title">{destination.title || `(untitled ${destination.id.slice(0, 8)})`}</div>
                  {destination.subtitle && <div className="sync-page-meta">{destination.subtitle}</div>}
                </button>
              ))}
            </div>
            {destinations.length === 0 && !error && (
              <p className="sync-empty-pages">
                {query.trim()
                  ? "No destinations found for this query."
                  : "No destinations found yet. You can still use default routing or create one."}
              </p>
            )}

            <label className="sync-label">Create new destination</label>
            <div className="sync-create-row">
              <input
                className="sync-search"
                placeholder="New destination title"
                value={newDestinationTitle}
                onChange={(e) => setNewDestinationTitle(e.target.value)}
              />
              <button
                className="sync-btn sync-btn--retry"
                type="button"
                onClick={handleCreateDestination}
                disabled={isCreating || !newDestinationTitle.trim()}
              >
                {isCreating ? "Creating..." : "Create"}
              </button>
            </div>
            {error && (
              <button className="sync-btn sync-btn--retry" onClick={() => fetchDestinations(adapter, query.trim())}>
                Retry loading destinations
              </button>
            )}
            <div className="sync-actions">
              <button className="sync-btn sync-btn--skip" onClick={() => onSelect(undefined)}>
                Skip
              </button>
              <button className="sync-btn sync-btn--primary" onClick={handleConfirm}>
                Confirm
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SyncDestinationPicker;
